import React, { FC, memo, useEffect, useMemo, useState } from 'react';
import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import usePrefix from 'utils/usePrefix';
import { ResponseError } from 'utils/api';
import { resqlExpertApi } from 'utils/api/resqlexpert';
import { ResqlExpertSubmissionResponse } from 'utils/api/resqlexpert.d';
import { SchoolResponse } from 'utils/api/schools';
import { notificationTypes } from 'utils/constants';
import { useApp } from 'App';
import { ActionTypes } from 'App/types';
import Modal from 'components/Modal';
import Loader from 'components/Loader';
import Button from 'components/Button';
import ConfirmModal from 'components/ConfirmModal';
import { CategoryKey, TABS, TITLE_CHARS_LIMIT } from 'components/ResqlExpert/constants';
import FormItemTitle from 'components/ResqlExpert/FormItemTitle';
import SchoolSelect from 'components/ResqlExpert/SchoolSelect';
import {
  FormActionsStyled,
  SubmissionBasicDataStyled,
  SubmissionFormStyled,
} from 'components/ResqlExpert/SubmissionForm/styles';
import SubmissionFormDescription from 'components/ResqlExpert/SubmissionForm/SubmissionFormDescription';
import SubmissionFormContent from 'components/ResqlExpert/SubmissionForm/SubmissionFormContent';

import Problem from './Problem';
import Source from './Source';
import Actions from './Actions';
import Participants from './Participants';
import Results from './Results';
import Retrospective from './Retrospective';
import {
  INITIAL_VALUES,
  SubmissionFormValues,
  ValidatedSubmissionFormValues,
  normalize,
} from './normalize';

export type { SubmissionFormValues };

const nonEmpty = (s?: string) => !!s && s.trim() !== '';
const someNonEmpty = (xs?: string[]) => xs?.some((s) => s.trim() !== '') ?? false;

export const hasAnyContent = (v: Partial<SubmissionFormValues> | undefined): boolean => {
  if (!v) return false;
  return (
    nonEmpty(v.situation_description) ||
    nonEmpty(v.actions_taken) ||
    nonEmpty(v.positive_outcomes) ||
    nonEmpty(v.negative_outcomes) ||
    nonEmpty(v.short_term_objectives) ||
    nonEmpty(v.long_term_objectives) ||
    someNonEmpty(v.information_sources) ||
    someNonEmpty(v.involved_parties)
  );
};

export const isAllContentFilled = (v: SubmissionFormValues): boolean =>
  nonEmpty(v.situation_description) &&
  nonEmpty(v.actions_taken) &&
  nonEmpty(v.positive_outcomes) &&
  nonEmpty(v.negative_outcomes) &&
  nonEmpty(v.short_term_objectives) &&
  nonEmpty(v.long_term_objectives) &&
  someNonEmpty(v.information_sources) &&
  someNonEmpty(v.involved_parties);

const validationSchema = (ty: (key: string, vars?: object) => string) =>
  Yup.object().shape({
    title: Yup.string().trim().required(ty('field_required')).max(TITLE_CHARS_LIMIT),
    school_id: Yup.number().required(ty('field_required')),
  });

const validateContent = (
  values: SubmissionFormValues,
  ty: (key: string, vars?: object) => string,
): Record<string, string> => (hasAnyContent(values) ? {} : { _content: ty('submission_empty') });

interface FormBodyProps {
  schools: SchoolResponse[];
}

const FormBody: FC<FormBodyProps> = ({ schools }) => {
  const t = usePrefix('ResqlExpert');
  const [activeTab, setActiveTab] = useState<CategoryKey>(TABS[0]);
  const [showConfirm, setShowConfirm] = useState(false);
  const { values, setFieldValue, isSubmitting, isValid, submitForm } =
    useFormikContext<SubmissionFormValues>();

  const canSubmit = isValid && !isSubmitting;

  const handleConfirm = () => {
    setShowConfirm(false);
    submitForm();
  };

  return (
    <SubmissionFormStyled>
      <SubmissionFormDescription />
      <SubmissionBasicDataStyled>
        <SchoolSelect name="school_id" schools={schools} />
        <FormItemTitle
          id="submission-title"
          isRequired={true}
          label={t('submission_name')}
          placeholder={t('submission_name')}
          value={values.title}
          onChange={(v) => setFieldValue('title', v)}
        />
      </SubmissionBasicDataStyled>
      <SubmissionFormContent
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        tabMap={{
          PROBLEM: <Problem />,
          SOURCE: <Source />,
          ACTIONS: <Actions />,
          PARTICIPANTS: <Participants />,
          RESULTS: <Results />,
          RETROSPECTIVE: <Retrospective />,
        }}
      />
      <FormActionsStyled>
        <Button
          label={t('cta_send_form')}
          onClick={() => setShowConfirm(true)}
          color="primary"
          disabled={!canSubmit}
        />
      </FormActionsStyled>
      {showConfirm && (
        <ConfirmModal
          title={t('confirm_send_title')}
          text={isAllContentFilled(values) ? t('confirm_send_complete') : t('confirm_send_incomplete')}
          onCancel={() => setShowConfirm(false)}
          onConfirm={handleConfirm}
        />
      )}
    </SubmissionFormStyled>
  );
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  fetchData: () => Promise<unknown>;
  onCreated?: (submission: ResqlExpertSubmissionResponse) => void;
}

const SubmissionForm: FC<Props> = ({ isOpen, onClose, fetchData, onCreated }) => {
  const t = usePrefix('ResqlExpert');
  const ty = usePrefix('YupErrors');
  const schema = useMemo(() => validationSchema(ty), [ty]);
  const [, dispatch] = useApp();
  const [schools, setSchools] = useState<SchoolResponse[] | undefined>(undefined);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    resqlExpertApi
      .getSchools()
      .then((list) => {
        if (!cancelled) setSchools(list);
      })
      .catch((error) => {
        const typedError = error as ResponseError;
        dispatch({
          type: ActionTypes.SET_NOTIFICATION_CODE,
          payload: { code: typedError?.parsed?.code, type: notificationTypes.error },
        });
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, dispatch]);

  const onSubmit = async (values: SubmissionFormValues) => {
    if (values.school_id === undefined) return;
    const validated: ValidatedSubmissionFormValues = { ...values, school_id: values.school_id };
    try {
      const created = await resqlExpertApi.createSubmission(normalize(validated));
      dispatch({
        type: ActionTypes.SET_NOTIFICATION_CODE,
        payload: { type: notificationTypes.success },
      });
      await fetchData();
      onCreated?.(created);
      onClose();
    } catch (error) {
      const typedError = error as ResponseError;
      dispatch({
        type: ActionTypes.SET_NOTIFICATION_CODE,
        payload: { code: typedError?.parsed?.code, type: notificationTypes.error },
      });
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} paddingTop="24px">
      {!schools ? (
        <Loader label={t('loading')} />
      ) : (
        <Formik
          initialValues={INITIAL_VALUES}
          validationSchema={schema}
          validate={(values) => validateContent(values, ty)}
          validateOnMount
          onSubmit={onSubmit}
        >
          <FormBody schools={schools} />
        </Formik>
      )}
    </Modal>
  );
};

export default memo(SubmissionForm);
